import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import { AttributeValueModel } from "@insite/client-framework/Types/ApiModels";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps, GridWidths } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps {
    maximumNumberAttributeTypes: number;
    extendedStyles?: ProductAttributesStyles;
    customerProductNumber?: string;
    manufacturerItem?: string;
    showCustomerName?: boolean;
    showManufacturerItem?: boolean;
}

type Props = OwnProps & HasProduct;

export interface ProductAttributesStyles {
    container?: GridContainerProps;
    attributeValueText?: TypographyPresentationProps;
    attributeTypeGridItem?: GridItemProps;
    condensed?: boolean;
    attributeTypeText?: TypographyPresentationProps;
    attributeValueGridItem?: TypographyPresentationProps;
}

export const productAttributesStyles: ProductAttributesStyles = {
    container: {
        gap: 0,
        css: css`
            > ${GridOffset} {
                flex-wrap: wrap;
            }
        `,
    },
    attributeTypeGridItem: {
        css: css`
            padding: 1px 0px;
            width: 100%;
            display: flex;
            flex-direction: column;
        `,
    },
    attributeValueText: {
        size: 14,
        css: css`
            margin-left: 3px;
        `,
    },
    attributeTypeText: {
        css: css`
            flex: 0 0 auto;
            font-size: 14px;
            font-family:"Roboto-Bold"
            color: ${getColor("text.accent")};
        `,
    },
    attributeValueGridItem: {
        color: "text.main",
        css: css`
            flex: 1 1 100%;
            font-size: 14px;
            min-width: 0;
            color: ${getColor("text.accent")};
        `,
    },
};

const ProductAttributes: FC<Props> = ({
    product: { attributeTypes },
    maximumNumberAttributeTypes,
    extendedStyles,
    customerProductNumber,
    manufacturerItem,
    showCustomerName = true,
    showManufacturerItem = true,
}) => {
    const [styles] = React.useState(() => mergeToNew(productAttributesStyles, extendedStyles));
    const displayManufacturerNumber = showManufacturerItem && manufacturerItem;
    const displayCustomerNumber = showCustomerName && customerProductNumber;
    const numberOfItems = 1 + (displayManufacturerNumber ? 1 : 0) + (displayCustomerNumber ? 1 : 0);
    const mobileGridItemWidth = numberOfItems > 1 ? 6 : 12;
    const desktopGridItemWidth = 12 / numberOfItems;
    const gridItemWidth = styles.condensed
        ? [mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, mobileGridItemWidth, desktopGridItemWidth]
        : 12;

    if (!attributeTypes || attributeTypes.length === 0) {
        return null;
    }

    const maximum = Math.min(maximumNumberAttributeTypes, 100);
    const limitedAttributeTypes = attributeTypes.slice(0, maximum);

    const displayAttributeValues = (attributeValues: AttributeValueModel[] | null) => {
        return (
            <Typography {...styles.attributeValueText}>
                {(attributeValues ?? []).map(a => a.valueDisplay).join(", ")}
            </Typography>
        );
    };

    return (
        <GridContainer {...styles.container}>
            <GridItem width={gridItemWidth as GridWidths} {...styles.attributeTypeGridItem}>
                {limitedAttributeTypes.map(attributeType => (
                    <div style={{ width: "100%" }} key={attributeType.id}>
                        {/* Add a container div with width: 100% */}
                        <Typography
                            {...styles.attributeTypeText}
                            data-test-selector={`attributeType${attributeType.id}`}
                        >
                            {attributeType.label || attributeType.name}:&nbsp;
                        </Typography>
                        <Typography
                            {...styles.attributeValueGridItem}
                            data-test-selector={`attributeValuesFor${attributeType.id}`}
                            style={{
                                display:
                                    attributeType.attributeValues && attributeType.attributeValues.length > 10
                                        ? "block"
                                        : "inline",
                                width:
                                    attributeType.attributeValues && attributeType.attributeValues.length > 10
                                        ? "100%"
                                        : "auto",
                                whiteSpace:
                                    attributeType.attributeValues && attributeType.attributeValues.length > 10
                                        ? "pre-wrap"
                                        : "initial",
                            }}
                        >
                            {displayAttributeValues(attributeType.attributeValues)}
                        </Typography>
                    </div>
                ))}
            </GridItem>
        </GridContainer>
    );
};
export default withProduct(ProductAttributes);
